import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const VIEW_MODES = ['single', 'double'];

/**
 * Custom hook to handle all chapter viewer navigation logic
 */
function useViewerNavigation({
  chapter,
  chapterId,
  pageNumber,
  viewMode,
  frames,
  framesByMode,
  currentFrameIndex,
  navigableChapters,
  accessToken
}) {
  const navigate = useNavigate();

  // Helper to build URLs with access token
  const buildNavUrl = useCallback((path) => {
    if (accessToken) {
      return `${path}?access=${accessToken}`;
    }
    return path;
  }, [accessToken]);

  // Navigate to a specific frame
  const navigateToFrame = useCallback((frame) => {
    if (!frame) return;
    const targetPage = Math.min(...frame.pages);
    navigate(buildNavUrl(`/chapter/${chapterId}/page/${targetPage}`));
  }, [chapterId, navigate, buildNavUrl]);

  // Frame navigation
  const goToNextFrame = useCallback(() => {
    if (!frames.length || currentFrameIndex < 0) return;
    const nextFrame = frames[currentFrameIndex + 1];
    if (nextFrame) {
      navigateToFrame(nextFrame);
    }
  }, [currentFrameIndex, frames, navigateToFrame]);

  const goToPreviousFrame = useCallback(() => {
    if (!frames.length || currentFrameIndex <= 0) return;
    const prevFrame = frames[currentFrameIndex - 1];
    if (prevFrame) {
      navigateToFrame(prevFrame);
    }
  }, [currentFrameIndex, frames, navigateToFrame]);

  // Chapter navigation
  const goToPreviousChapter = useCallback(() => {
    const currentIndex = navigableChapters.findIndex(ch => ch.id === chapterId);
    if (currentIndex > 0) {
      const prevChapter = navigableChapters[currentIndex - 1];
      navigate(buildNavUrl(`/chapter/${prevChapter.id}/page/0`));
    }
  }, [navigableChapters, chapterId, navigate, buildNavUrl]);

  const goToNextChapter = useCallback(() => {
    const currentIndex = navigableChapters.findIndex(ch => ch.id === chapterId);
    if (currentIndex >= 0 && currentIndex < navigableChapters.length - 1) {
      const nextChapter = navigableChapters[currentIndex + 1];
      navigate(buildNavUrl(`/chapter/${nextChapter.id}/page/0`));
    }
  }, [navigableChapters, chapterId, navigate, buildNavUrl]);

  // Direct navigation handlers
  const handleChapterChange = useCallback((nextChapterId) => {
    if (!nextChapterId) return;
    navigate(buildNavUrl(`/chapter/${nextChapterId}/page/0`));
  }, [navigate, buildNavUrl]);

  const handlePageChange = useCallback((nextPage) => {
    if (!Number.isInteger(nextPage)) return;
    navigate(buildNavUrl(`/chapter/${chapterId}/page/${nextPage}`));
  }, [chapterId, navigate, buildNavUrl]);

  // View mode change with page preservation
  const handleViewModeChange = useCallback((nextMode) => {
    if (!VIEW_MODES.includes(nextMode)) return;
    
    const targetFrames = nextMode === 'double' ? framesByMode.double : framesByMode.single;
    const matchingFrame = targetFrames.find(frame => frame.pages.includes(pageNumber));
    
    if (matchingFrame) {
      const targetPage = Math.min(...matchingFrame.pages);
      navigate(buildNavUrl(`/chapter/${chapterId}/page/${targetPage}`));
    }
  }, [chapterId, framesByMode, navigate, pageNumber, buildNavUrl]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (event) => {
      if (event.key === 'ArrowRight') {
        goToNextFrame();
      } else if (event.key === 'ArrowLeft') {
        goToPreviousFrame();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goToNextFrame, goToPreviousFrame]);

  return {
    goToNextFrame,
    goToPreviousFrame,
    goToNextChapter,
    goToPreviousChapter,
    handleChapterChange,
    handlePageChange,
    handleViewModeChange,
    navigateToFrame,
    buildNavUrl
  };
}

export default useViewerNavigation;

